import React from "react";
import { Select } from "@jkast90/kast-common/ui";
import { useMediaQuery } from "@jkast90/kast-common/ui";
import { useWorkspace } from "@workspace/hooks/useReduxWorkspace";
import { useAuth } from "@common/hooks/useAuth";
import { emptyPlaceholder } from "./workspacePlaceholder";
import type { WorkspaceSummary } from "../../store/slices/workspaceSlice";
import styles from "./WorkspaceSelector.module.css";

/**
 * WorkspaceSelector Component
 *
 * The current workspace's name shown as a title, with a chevron that opens
 * the list (kast-common's Select, bare, restyled). Syncs with the Redux
 * workspace state; the slice persists the selection under the signed-in
 * user's key. Type to jump to a name, arrow keys and Enter to pick, Escape to
 * close.
 *
 * It stays the app's own component, not a shared one, because it is the
 * workspace state's view: its placeholder says whether the list is empty,
 * loading or signed out (workspacePlaceholder), and it shortens long names
 * on a narrow screen.
 *
 * @param {Object} props
 * @param {string} props.className - Optional CSS class name for styling
 * @param {Object} props.style - Optional inline styles
 * @param {boolean} props.showTooltip - Whether to show tooltip (default: true)
 * @param {string} props.tooltipText - Custom tooltip text (default: "Select workspace")
 * @param {Function} props.onChange - Optional callback when workspace changes
 */
export function WorkspaceSelector({
  className = "",
  style = {},
  showTooltip = true,
  tooltipText = "Select workspace",
  onChange = undefined,
}: {
  className?: string;
  style?: React.CSSProperties;
  showTooltip?: boolean;
  tooltipText?: string;
  onChange?: (workspace: WorkspaceSummary) => void;
}) {
  const { workspaces, selectedWorkspace, setSelectedWorkspace, loading } =
    useWorkspace();
  const { isAuthenticated, isReady } = useAuth();
  const isCompact = useMediaQuery("(max-width: 624px)");

  // Long names are cut short on a small screen.
  const shown = (name: string) =>
    isCompact && name.length > 12 ? name.substring(0, 12) + "..." : name;

  const options = workspaces.map((workspace) => ({
    value: String(workspace.id),
    label: shown(workspace.name || `Workspace ${workspace.id}`),
  }));

  const handleWorkspaceChange = (id: string) => {
    const workspace = workspaces.find((ws) => String(ws.id) === id);
    if (workspace) {
      // The slice persists it, under the signed-in user's key.
      setSelectedWorkspace(workspace);
      onChange?.(workspace);
    }
  };

  const currentWorkspaceId = selectedWorkspace?.id;

  const dropdownElement = (tip?: string) => (
    <div
      className={`${styles.title} ${isCompact ? styles.compact : ""} ${className}`}
      data-tip={tip || undefined}
      style={style}
    >
      <Select
        variant="bare"
        className={styles.trigger}
        aria-label="Select workspace"
        size="sm"
        options={options}
        value={
          currentWorkspaceId === undefined ? null : String(currentWorkspaceId)
        }
        onChange={handleWorkspaceChange}
        placeholder={emptyPlaceholder(
          workspaces.length,
          isAuthenticated,
          isReady,
          loading,
          isCompact,
        )}
        disabled={workspaces.length === 0}
        emptyText="No workspaces found"
      />
    </div>
  );

  if (showTooltip) {
    // Show the full workspace name in the tooltip if it's cut short
    const fullName =
      workspaces.find((ws) => ws.id === currentWorkspaceId)?.name || "";
    const displayTooltip =
      isCompact && fullName.length > 12
        ? `${tooltipText}: ${fullName}`
        : tooltipText;

    return dropdownElement(displayTooltip);
  }

  return dropdownElement();
}

export default WorkspaceSelector;
